import React, { useCallback, useMemo, useRef } from 'react';
import { consolidateTotalsOntoOwnSheet, refineThermalPagesWithDomVerify } from './thermalPageDomRefine';
import PrintPackaginSlipFitCrunch from '../printPackaginSlipFitCrunch';
import PrintPackaginSlipMIXT from '../printPackagingSlipMIXT';
import {
    measureFitCrunchOrderItemRowHeights,
    measureMixtOrderItemRowHeights,
    renderFitCrunchOrderItemRowHtml,
    renderMixtOrderItemRowHtml,
} from './packingSlipPrintRowHtml';
import {
    getThermalBusyPlaceholderStyle,
    isFitCrunchSalesChannel,
    THERMAL_PAGE_HEIGHT_IN,
    THERMAL_PAGE_WIDTH_CSS,
} from './thermalPrintConstants';
import { usePrintPagination } from './usePrintPagination';

export interface PaginatedPackingSlipOrderProps {
    order: any;
}

/**
 * Off-screen measurement layer + usePrintPagination row-height slicing for one order.
 */
const PaginatedPackingSlipOrder: React.FC<PaginatedPackingSlipOrderProps> = ({ order }) => {
    const lineItems = useMemo(() => order?.order_item_info ?? [], [order]);

    const headerRef = useRef<HTMLDivElement>(null);
    const continuationHeaderRef = useRef<HTMLDivElement>(null);
    const footerRef = useRef<HTMLDivElement>(null);
    const footerPeelOnlyRef = useRef<HTMLDivElement>(null);
    const measureContainerRef = useRef<HTMLDivElement>(null);

    const isFitCrunch = isFitCrunchSalesChannel(order?.sales_channel_id);
    const PackingSlip = isFitCrunch ? PrintPackaginSlipFitCrunch : PrintPackaginSlipMIXT;

    const renderRow = useCallback(
        (row: any) =>
            isFitCrunch ? renderFitCrunchOrderItemRowHtml(row) : renderMixtOrderItemRowHtml(row),
        [isFitCrunch]
    );

    const measureRowHeights = useCallback(
        (data: any[], container: HTMLElement, rowHtml: (row: any) => string) =>
            isFitCrunch
                ? measureFitCrunchOrderItemRowHeights(container, data, rowHtml)
                : measureMixtOrderItemRowHeights(container, data, rowHtml),
        [isFitCrunch],
    );

    const verifyPostProcess = useCallback(
        (chunks: any[][]) => {
            const refined = refineThermalPagesWithDomVerify(chunks, order, PackingSlip);
            return consolidateTotalsOntoOwnSheet(refined, order, PackingSlip);
        },
        [order, PackingSlip],
    );

    const { pages } = usePrintPagination({
        data: lineItems,
        renderRow,
        measureRowHeights,
        pageHeightIn: THERMAL_PAGE_HEIGHT_IN,
        padding: 20,
        headerRef,
        continuationHeaderRef,
        footerRef,
        footerPeelOnlyRef,
        measureContainerRef,
        verifyPostProcess,
    });

    const orderWithItems = (chunk: any[]) => ({
        orderDetails: {
            ...order,
            order_item_info: chunk,
        },
    });

    const showSlips = lineItems.length === 0 || pages.length > 0;

    return (
        <>
            <div
                aria-hidden
                className="packing-slip-print-measure"
                style={{
                    position: 'absolute',
                    left: -9999,
                    top: 0,
                    width: THERMAL_PAGE_WIDTH_CSS,
                    pointerEvents: 'none',
                    visibility: 'hidden',
                }}
            >
                <div ref={headerRef}>
                    <PackingSlip {...orderWithItems(lineItems)} slipVariant="chrome-header" />
                </div>
                <div ref={continuationHeaderRef}>
                    <PackingSlip {...orderWithItems(lineItems)} slipVariant="chrome-continuation-header" />
                </div>
                <div ref={footerRef}>
                    <PackingSlip {...orderWithItems(lineItems)} slipVariant="chrome-footer" />
                </div>
                <div ref={footerPeelOnlyRef}>
                    <PackingSlip {...orderWithItems(lineItems)} slipVariant="chrome-footer-peel-only" />
                </div>
                <div ref={measureContainerRef} />
            </div>

            {!showSlips && (
                <div
                    className="packing-slip-peel-page"
                    style={getThermalBusyPlaceholderStyle()}
                    aria-busy="true"
                />
            )}
            {showSlips &&
                pages.map((chunk, index) => {
                    const multiPage = pages.length > 1;
                    const showOrderTotals = !multiPage || index === pages.length - 1;
                    return (
                        <div
                            key={`${order?.order_number ?? 'o'}-${index}`}
                            className={`packing-slip-peel-page${index > 0 ? ' packing-slip-peel-page--continuation' : ''}`}
                            style={{ lineHeight: 1.2 }}
                        >
                            <PackingSlip
                                {...orderWithItems(chunk)}
                                continuationPage={index > 0}
                                showOrderTotals={showOrderTotals}
                            />
                        </div>
                    );
                })}
        </>
    );
};

export default PaginatedPackingSlipOrder;
